import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths } from 'date-fns';
import { ChevronLeft, ChevronRight, CalendarRange, CalendarDays, X, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { formatDate, formatCurrency } from '../../lib/utils';
import { PRIORITY_COLORS, getEventPriorityType } from './eventPriority';

// Check if an event occurs on a specific date string (YYYY-MM-DD)
const eventSpansDate = (event, dateStr) => {
  // If event has recurring_schedule with computed_dates, use ONLY those
  if (event.recurring_schedule?.computed_dates?.length > 0) {
    return event.recurring_schedule.computed_dates.includes(dateStr);
  }

  // Check primary date
  if (event.date === dateStr) return true;

  // Check if dateStr falls within primary date range
  if (event.date && event.end_date) {
    const [year, month, day] = dateStr.split('-').map(Number);
    const checkDate = new Date(year, month - 1, day);
    const [sy, sm, sd] = event.date.split('-').map(Number);
    const startDate = new Date(sy, sm - 1, sd);
    const [ey, em, ed] = event.end_date.split('-').map(Number);
    const endDate = new Date(ey, em - 1, ed);
    if (checkDate >= startDate && checkDate <= endDate) return true;
  }

  // Check additional date ranges
  if (event.event_dates && Array.isArray(event.event_dates)) {
    for (const range of event.event_dates) {
      if (range.start_date === dateStr) return true;
      if (range.start_date && range.end_date) {
        const [year, month, day] = dateStr.split('-').map(Number);
        const checkDate = new Date(year, month - 1, day);
        const [sy, sm, sd] = range.start_date.split('-').map(Number);
        const startDate = new Date(sy, sm - 1, sd);
        const [ey, em, ed] = range.end_date.split('-').map(Number);
        const endDate = new Date(ey, em - 1, ed);
        if (checkDate >= startDate && checkDate <= endDate) return true;
      }
    }
  }

  return false;
};

// Mini month component
const MiniMonth = ({ month, events, onEventClick, hoveredEvent, setHoveredEvent, applicationStatusMap, favoriteIds }) => {
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Get the day of week for the first day (0 = Sunday)
  const startDay = monthStart.getDay();

  // Create array with empty slots for alignment
  const calendarDays = [...Array(startDay).fill(null), ...days];

  // Get events for a specific day using the updated eventSpansDate logic
  const getEventsForDay = (day) => {
    if (!day) return [];
    const year = day.getFullYear();
    const month = String(day.getMonth() + 1).padStart(2, '0');
    const d = String(day.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${d}`;
    return events.filter(event => eventSpansDate(event, dateStr));
  };

  const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-2 h-full flex flex-col">
      {/* Month Header */}
      <div className="text-center font-bold text-[#1E3A5F] text-sm mb-1 py-1 bg-gray-50 rounded">
        {format(month, 'MMMM yyyy')}
      </div>

      {/* Week Day Headers */}
      <div className="grid grid-cols-7 gap-0 mb-1">
        {weekDays.map((day, i) => (
          <div key={i} className="text-center text-[10px] font-medium text-gray-400 py-0.5">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Days */}
      <div className="grid grid-cols-7 gap-0 flex-1">
        {calendarDays.map((day, index) => {
          const dayEvents = day ? getEventsForDay(day) : [];
          const hasEvents = dayEvents.length > 0;
          const isToday = day && isSameDay(day, new Date());

          return (
            <div
              key={index}
              className={`
                relative min-h-[28px] p-0.5 text-center text-xs border-t border-gray-100
                ${!day ? 'bg-gray-50' : ''}
                ${isToday ? 'bg-[#FFEDDF]' : ''}
              `}
            >
              {day && (
                <>
                  <span className={`text-[10px] ${isToday ? 'font-bold text-[#1E3A5F]' : 'text-gray-600'}`}>
                    {format(day, 'd')}
                  </span>

                  {/* Event Dots - colored by priority */}
                  {hasEvents && (
                    <div className="flex flex-wrap justify-center gap-0.5 mt-0.5">
                      {dayEvents.slice(0, 3).map((event) => {
                        const priorityType = getEventPriorityType(event, applicationStatusMap, favoriteIds);
                        const colors = PRIORITY_COLORS[priorityType] || PRIORITY_COLORS.community;
                        const isHovered = hoveredEvent?.id === event.id;

                        return (
                          <div
                            key={event.id}
                            className="relative group"
                            onMouseEnter={() => setHoveredEvent(event)}
                            onMouseLeave={() => setHoveredEvent(null)}
                            data-testid={`event-dot-${event.id}`}
                          >
                            <Link to={`/events/${event.id}`}>
                              <div
                                className={`w-2 h-2 rounded-full cursor-pointer transition-transform ${colors.dot} ${isHovered ? 'scale-150 ring-2 ring-white ring-offset-1' : ''}`}
                                title={event.title}
                              />
                            </Link>
                          </div>
                        );
                      })}
                      {dayEvents.length > 3 && (
                        <span className="text-[8px] text-gray-500">+{dayEvents.length - 3}</span>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Event Popup component
const EventPopup = ({ event, onClose, applicationStatusMap }) => {
  if (!event) return null;

  const appStatus = applicationStatusMap?.[event.id];
  const priorityType = getEventPriorityType(event, applicationStatusMap, new Set());
  const colors = PRIORITY_COLORS[priorityType] || PRIORITY_COLORS.community;

  return (
    <div className={`fixed z-50 top-4 right-4 w-80 bg-white rounded-xl shadow-2xl border-2 ${colors.border} overflow-hidden animate-in slide-in-from-right-5 duration-200`} data-testid="year-view-popup">
      {/* Header */}
      <div className={`px-4 py-3 ${colors.bg} flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${colors.dot}`} />
          <span className={`text-xs font-medium ${colors.text}`} data-testid="popup-event-type">
            {colors.label}
            {appStatus && <span className="ml-1 opacity-75">({appStatus.status})</span>}
          </span>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="popup-close-btn">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-bold text-[#1E3A5F] text-lg mb-2 line-clamp-2" data-testid="popup-event-title">{event.title}</h3>

        <div className="space-y-2 text-sm text-gray-600">
          <div className="flex items-center gap-2" data-testid="popup-event-date">
            <CalendarDays className="w-4 h-4 text-[#FE5F55]" />
            <span>
              {formatDate(event.date)}
              {event.end_date && event.end_date !== event.date && ` - ${formatDate(event.end_date)}`}
            </span>
          </div>

          {event.recurring_schedule?.computed_dates?.length > 0 && (
            <div className="flex items-center gap-2 text-emerald-600" data-testid="popup-recurring-info">
              <CalendarRange className="w-4 h-4" />
              <span>{event.recurring_schedule.computed_dates.length} market days</span>
            </div>
          )}

          {event.city && (
            <div className="flex items-center gap-2" data-testid="popup-event-location">
              <span className="text-gray-400">Location:</span>
              <span>{event.city}{event.state ? `, ${event.state}` : ''}</span>
            </div>
          )}

          {event.booth_cost > 0 && (
            <div className="flex items-center gap-2" data-testid="popup-event-cost">
              <span className="text-gray-400">Booth Cost:</span>
              <span className="font-medium">{formatCurrency(event.booth_cost)}</span>
            </div>
          )}

          {event.event_type && (
            <div className="flex items-center gap-2" data-testid="popup-event-type-info">
              <span className="text-gray-400">Type:</span>
              <span className="capitalize">{event.event_type.replace('_', ' ')}</span>
            </div>
          )}
        </div>

        {/* Action Button */}
        <Link to={`/events/${event.id}`}>
          <Button className="w-full mt-4 bg-[#1E3A5F] hover:bg-[#152a47] text-white" data-testid="popup-view-details-btn">
            View Details
          </Button>
        </Link>
      </div>
    </div>
  );
};

// Main Year View Component
export const YearView = ({ events = [], userCreatedEvents = [], favoriteIds = new Set(), applicationStatusMap = {} }) => {
  const [startMonth, setStartMonth] = useState(() => startOfMonth(new Date()));
  const [monthCount, setMonthCount] = useState(6);
  const [hoveredEvent, setHoveredEvent] = useState(null);

  // Enrich events with additional flags
  const enrichedEvents = useMemo(() => {
    const userCreatedIds = new Set(userCreatedEvents.map(e => e.id));

    return events.map(event => ({
      ...event,
      is_favorited: favoriteIds.has(event.id),
      created_by_current_user: userCreatedIds.has(event.id)
    }));
  }, [events, userCreatedEvents, favoriteIds]);

  // Generate array of months to display
  const monthsToDisplay = useMemo(() => {
    const months = [];
    for (let i = 0; i < monthCount; i++) {
      months.push(addMonths(startMonth, i));
    }
    return months;
  }, [startMonth, monthCount]);

  // Navigate months
  const goToPreviousPeriod = () => setStartMonth(prev => subMonths(prev, monthCount));
  const goToNextPeriod = () => setStartMonth(prev => addMonths(prev, monthCount));
  const goToToday = () => setStartMonth(startOfMonth(new Date()));

  // Calculate grid layout
  const gridCols = monthCount <= 6 ? 3 : monthCount <= 8 ? 4 : monthCount === 9 ? 3 : 4;
  const gridRows = Math.ceil(monthCount / gridCols);

  return (
    <div className="h-full flex flex-col" data-testid="year-view">
      {/* Controls Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4 bg-white p-4 rounded-xl border-2 border-[#1E3A5F]">
        <div className="flex items-center gap-3">
          <CalendarRange className="w-5 h-5 text-[#1E3A5F]" />
          <span className="font-semibold text-[#1E3A5F]">Year View</span>
        </div>

        {/* Month Count Selector */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Show:</span>
          <Select value={monthCount.toString()} onValueChange={(v) => setMonthCount(parseInt(v))}>
            <SelectTrigger className="w-28 h-9 border-[#1E3A5F]" data-testid="year-view-month-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="6" data-testid="month-option-6">6 months</SelectItem>
              <SelectItem value="8" data-testid="month-option-8">8 months</SelectItem>
              <SelectItem value="9" data-testid="month-option-9">9 months</SelectItem>
              <SelectItem value="12" data-testid="month-option-12">12 months</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Navigation */}
        <div className="flex items-center gap-2" data-testid="year-view-navigation">
          <Button variant="outline" size="sm" onClick={goToPreviousPeriod} className="border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#FFEDDF]" data-testid="year-view-prev-btn">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToToday} className="border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#FFEDDF] px-4" data-testid="year-view-today-btn">
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={goToNextPeriod} className="border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#FFEDDF]" data-testid="year-view-next-btn">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Legend - Full priority colors */}
        <div className="flex flex-wrap items-center gap-3 text-xs" data-testid="year-view-legend">
          <div className="flex items-center gap-1" data-testid="legend-accepted">
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-600" />
            <span className="text-gray-600">Accepted</span>
          </div>
          <div className="flex items-center gap-1" data-testid="legend-in-progress">
            <div className="w-2.5 h-2.5 rounded-full bg-purple-600" />
            <span className="text-gray-600">In Progress</span>
          </div>
          <div className="flex items-center gap-1" data-testid="legend-waitlisted">
            <div className="w-2.5 h-2.5 rounded-full bg-orange-500" />
            <span className="text-gray-600">Waitlisted</span>
          </div>
          <div className="flex items-center gap-1" data-testid="legend-rejected">
            <div className="w-2.5 h-2.5 rounded-full bg-rose-500" />
            <span className="text-gray-600">Rejected</span>
          </div>
          <div className="flex items-center gap-1" data-testid="legend-saved">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
            <span className="text-gray-600">Saved</span>
          </div>
          <div className="flex items-center gap-1" data-testid="legend-my-events">
            <div className="w-2.5 h-2.5 rounded-full bg-gray-500" />
            <span className="text-gray-600">My Events</span>
          </div>
          <div className="flex items-center gap-1" data-testid="legend-community">
            <div className="w-2.5 h-2.5 rounded-full bg-cyan-500" />
            <span className="text-gray-600">Community</span>
          </div>
        </div>
      </div>

      {/* Date Range Display */}
      <div className="text-center mb-3" data-testid="year-view-date-range">
        <span className="text-lg font-semibold text-[#1E3A5F]">
          {format(monthsToDisplay[0], 'MMMM yyyy')} - {format(monthsToDisplay[monthsToDisplay.length - 1], 'MMMM yyyy')}
        </span>
      </div>

      {/* Month Grid */}
      <div
        className="flex-1 grid gap-3"
        style={{
          gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
          gridTemplateRows: `repeat(${gridRows}, 1fr)`,
          minHeight: '500px'
        }}
      >
        {monthsToDisplay.map((month) => (
          <MiniMonth
            key={format(month, 'yyyy-MM')}
            month={month}
            events={enrichedEvents}
            hoveredEvent={hoveredEvent}
            setHoveredEvent={setHoveredEvent}
            applicationStatusMap={applicationStatusMap}
            favoriteIds={favoriteIds}
          />
        ))}
      </div>

      {/* Event Popup */}
      {hoveredEvent && (
        <EventPopup
          event={hoveredEvent}
          onClose={() => setHoveredEvent(null)}
          applicationStatusMap={applicationStatusMap}
        />
      )}
    </div>
  );
};

export default YearView;
